import { MAX_OFFLINE_SECONDS } from './constants';
import type { GameState } from './state';
import { normalizeState } from './validation';

/** The subset of the Web Storage API we rely on (injectable for tests). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const SAVE_KEY = 'petal-plains:save';
const SAVE_FORMAT = 1;

interface SaveFile {
  format: number;
  savedAt: number;
  state: GameState;
}

export interface LoadedGame {
  state: GameState;
  /** Real seconds since the save was written, capped to MAX_OFFLINE_SECONDS. */
  offlineSeconds: number;
}

export function serializeSave(state: GameState, now: number): string {
  const file: SaveFile = { format: SAVE_FORMAT, savedAt: now, state };
  return JSON.stringify(file);
}

export function deserializeSave(json: string, now: number): LoadedGame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const file = parsed as Partial<SaveFile>;
  if (file.format !== SAVE_FORMAT) return null;
  const state = normalizeState(file.state);
  if (!state) return null;
  const savedAt = typeof file.savedAt === 'number' ? file.savedAt : now;
  const offlineSeconds = Math.min(MAX_OFFLINE_SECONDS, Math.max(0, (now - savedAt) / 1000));
  return { state, offlineSeconds };
}

export function saveGame(storage: KeyValueStorage, state: GameState, now = Date.now()): boolean {
  try {
    storage.setItem(SAVE_KEY, serializeSave(state, now));
    return true;
  } catch (error) {
    // Quota exceeded or storage disabled (private mode): keep playing in memory.
    console.warn('Petal Plains: could not save game', error);
    return false;
  }
}

export function loadGame(storage: KeyValueStorage, now = Date.now()): LoadedGame | null {
  let json: string | null;
  try {
    json = storage.getItem(SAVE_KEY);
  } catch {
    return null;
  }
  if (!json) return null;
  const loaded = deserializeSave(json, now);
  if (!loaded) {
    // Keep the unreadable save around rather than silently destroying progress.
    try {
      storage.setItem(`${SAVE_KEY}:corrupt`, json);
    } catch {
      /* ignore */
    }
  }
  return loaded;
}

export function deleteSave(storage: KeyValueStorage): void {
  try {
    storage.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** Returns the browser's localStorage, or an in-memory fallback if unavailable. */
export function browserStorage(): KeyValueStorage {
  try {
    const probe = '__petal_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    const memory = new Map<string, string>();
    return {
      getItem: (k) => memory.get(k) ?? null,
      setItem: (k, v) => void memory.set(k, v),
      removeItem: (k) => void memory.delete(k),
    };
  }
}
